import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@staff-app/ui';
import type { AmazonCoupon } from '@staff-app/shared-types';
import { CopyButton } from '@/components/CopyButton';
import { formatDiscount } from '../hooks/useAmazonCoupons';

const fmt = (d?: string | null) => {
  if (!d) return '—';
  const parsed = new Date(d);
  return isNaN(+parsed) ? '—' : parsed.toLocaleDateString();
};

const MARKET_URL: Record<string, string> = {
  us: 'amazon.com',
  uk: 'amazon.co.uk',
  de: 'amazon.de',
};

/**
 * "Coupon allocated" confirmation, with the sentence the staffer is about to paste into an email.
 *
 * Rendered by the PAGE rather than the form, for the reason MintedCouponDialog gives: a successful
 * allocate refetches the ledger underneath, and a dialog owned by the thing that re-renders is a
 * dialog that can vanish before it is read.
 *
 * IT NAMES BOTH DATES. The advisory expiry is what our ledger will show, and the campaign end is
 * what Amazon will actually enforce — a confirmation that printed only the first would be the
 * exact moment a staffer learns the wrong date and repeats it to a customer.
 *
 * It also spells out where the code goes in at checkout. That one line is the difference between
 * "the discount isn't working" and a redemption: the code applies at the payment step, not on the
 * product page, and customers reliably look for it on the listing first.
 */
export function AllocatedCouponDialog({
  coupon,
  onClose,
}: {
  coupon: AmazonCoupon | null;
  onClose: () => void;
}) {
  const host = coupon ? (MARKET_URL[coupon.marketplace] ?? 'Amazon') : 'Amazon';
  const customerText = coupon
    ? `Your discount code is ${coupon.code}, good for ` +
      `${formatDiscount(coupon.discountAmount, coupon.currency)} on ${host} ` +
      `through ${fmt(coupon.campaignEndsAt)}. Add the item to your cart, then enter the code at ` +
      `checkout under "Gift cards & promotional codes" — the discount appears at the payment ` +
      `step, not on the product page. Single use, not combinable with other offers.`
    : '';

  return (
    <Dialog open={coupon != null} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent data-testid="amazon-allocated-dialog">
        <DialogHeader>
          <DialogTitle>{'Code allocated — now send it'}</DialogTitle>
          <DialogDescription>
            {coupon
              ? `Reserved for ${coupon.customerEmail} and attributed to you. Nothing has been ` +
                'sent — copy the message below into your reply. The code is also in the table ' +
                'below if you need it again.'
              : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-3">
          <code
            className="flex-1 select-all font-mono text-lg tracking-wider"
            data-testid="amazon-allocated-code"
          >
            {coupon?.code}
          </code>
          {coupon ? (
            <CopyButton
              value={coupon.code}
              label={`Copy ${coupon.code}`}
              testId="amazon-copy-code-dialog"
            />
          ) : null}
        </div>

        {coupon ? (
          <>
            <p className="text-xs text-muted-foreground">
              {`Applies to ${coupon.sku} (${coupon.asin}) on ${host}.`}
            </p>
            {/* BOTH dates, labelled for what they are. See the docblock — this is the one screen
                where getting them confused reaches a customer. */}
            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
              <p data-testid="amazon-allocated-dates">
                <strong>{`Amazon stops accepting it on ${fmt(coupon.campaignEndsAt)}`}</strong>
                {` — that is the campaign end date, and it is the only one Amazon enforces.`}
              </p>
              <p className="mt-1 text-muted-foreground">
                {`Our ledger will show it as expired after ${fmt(coupon.expiresAt)}, which is a `}
                {'staff-side reminder only. Quote the Amazon date to the customer.'}
              </p>
            </div>

            <div className="rounded-md border p-3">
              <p className="mb-1 text-xs font-medium">
                {'Copy this into your reply — the app will not send it'}
              </p>
              <p className="text-xs text-muted-foreground" data-testid="amazon-customer-text">
                {customerText}
              </p>
              <div className="mt-2">
                <CopyButton
                  value={customerText}
                  label="Copy the customer message"
                  testId="amazon-copy-customer-text"
                />
              </div>
            </div>
          </>
        ) : null}

        <DialogFooter>
          <Button onClick={onClose} data-testid="amazon-allocated-done">
            {'Done'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
